import { PatientProviderTypeEnum } from "@mgdx/shared/src/models/Patient";
import { createContext, PropsWithChildren, useContext, useMemo } from "react";

import { useIsInAppView } from "../../hooks/useIsInAppView";
import { CurrentUserState, initialCurrentUserState as initialState } from "../../stores/currentUserStore";

type FeatureToggles = {
  medicineNotebook: {
    enabled: boolean;
  };
  phrNotebook: {
    enabled: boolean;
  };
  topView: {
    selectableCounseling: boolean;
  };
  pharmacySearch: {
    showInitialResult: boolean;
  };
  followUp: {
    editSendFollowUp: boolean;
  };
};

const defaultFeatureToggles: FeatureToggles = {
  medicineNotebook: {
    enabled: process.env.ENABLE_MEDICINE_NOTEBOOK === "true",
  },
  phrNotebook: {
    enabled: process.env.ENABLE_PHR_NOTEBOOK === "true" || process.env.MGDX_APP_NAME === "yqb",
  },
  topView: {
    selectableCounseling: process.env.ENABLE_SELECT_COUNSELING_TOP === "true",
  },
  pharmacySearch: {
    showInitialResult: process.env.ENABLE_INITIAL_PHARMACY_SEARCH === "true",
  },
  followUp: {
    editSendFollowUp: process.env.ENABLE_EDIT_SEND_FOLLOW_UP === "true",
  },
};

type AppStatusContextValue = {
  isInLineApp: CurrentUserState["isInLineApp"];
  isPharmacyDispensingEnabled: boolean;
  isPharmacyOnlineEnabled: boolean;
  isInApp: ReturnType<typeof useIsInAppView>;
  featureToggles: FeatureToggles;
};

const AppStatusContext = createContext<AppStatusContextValue>({
  isInLineApp: initialState.isLiffLogined,
  isPharmacyDispensingEnabled: process.env.DISABLE_DISPENSING !== "true",
  isPharmacyOnlineEnabled:
    process.env.ENABLE_ONLINE_COUNSELING !== "false" &&
    ((initialState.userInfo?.loginProviderType !== PatientProviderTypeEnum.LineSundrug &&
      initialState.userInfo?.loginProviderType !== PatientProviderTypeEnum.LineQol &&
      initialState.userInfo?.loginProviderType !== PatientProviderTypeEnum.LineTanpopo &&
      initialState.userInfo?.loginProviderType !== PatientProviderTypeEnum.LineNicho) ||
      process.env.ENABLE_ONLINE_COUNSELING === "true"),
  isInApp: false,
  featureToggles: defaultFeatureToggles,
});

export type AppStatusProviderPropsWithoutChildren = Omit<AppStatusContextValue, "featureToggles"> & {
  featureToggles?: Partial<FeatureToggles>;
};

export type AppStatusProviderProps = PropsWithChildren<AppStatusProviderPropsWithoutChildren>;

export const AppStatusProvider = ({
  children,
  isInApp,
  isInLineApp,
  isPharmacyDispensingEnabled,
  isPharmacyOnlineEnabled,
  featureToggles = defaultFeatureToggles,
}: PropsWithChildren<AppStatusProviderProps>) => {
  const value = useMemo<AppStatusContextValue>(
    () => ({
      isInLineApp,
      isPharmacyDispensingEnabled,
      isPharmacyOnlineEnabled,
      isInApp,
      featureToggles: {
        ...defaultFeatureToggles,
        ...featureToggles,
      },
    }),
    [isInLineApp, isPharmacyDispensingEnabled, isPharmacyOnlineEnabled, isInApp, featureToggles]
  );

  return <AppStatusContext.Provider value={value}>{children}</AppStatusContext.Provider>;
};

export const useAppStatus = (): AppStatusContextValue => useContext(AppStatusContext);
